import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getABTestVariant,
  trackABTestImpression,
  setABTestsFromDB,
  type ABTestVariant,
  type ABTestConfig,
} from '../abTesting';

/**
 * Hook that loads A/B test configs from the DB cache into the client-side store.
 * Call this once near the root of the app (e.g. in root.tsx or a layout).
 */
export function useABTestConfig() {
  const { data } = useQuery<ABTestConfig[]>({
    queryKey: ['ab-tests'],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) {
      setABTestsFromDB(data);
    }
  }, [data]);
}

/**
 * Hook to use A/B test variants in components.
 * Automatically tracks impressions and provides convenient helpers.
 *
 * @param testKey - Key of the test (matches `key` column in ab_test table)
 * @param trackImpression - Whether to automatically track impression (default: true)
 */
export function useABTest(
  testKey: string,
  trackImpression: boolean = true
): {
  variant: ABTestVariant;
  isVariant: boolean;
  isControl: boolean;
  isLoading: boolean;
} {
  const [variant, setVariant] = useState<ABTestVariant>('control');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userVariant = getABTestVariant(testKey);
    setVariant(userVariant);
    setIsLoading(false);

    if (trackImpression) {
      trackABTestImpression(testKey, userVariant);
    }
  }, [testKey, trackImpression]);

  return {
    variant,
    isVariant: variant === 'variant',
    isControl: variant === 'control',
    isLoading,
  };
}

/**
 * Hook that returns a boolean for simple show/hide tests.
 */
export function useABTestFlag(testKey: string): boolean {
  const { isVariant } = useABTest(testKey);
  return isVariant;
}
